import { HapticButton } from '_/components/ui/haptic-button'
import { createZxingBarcodeScanner } from '_/features/shopping/adapters/scanner/zxing-barcode-scanner'
import type { Barcode } from '_/features/shopping/ports/barcode-scanner'
import { createFileRoute } from '@tanstack/react-router'
import { useCallback, useEffect, useRef, useState } from 'react'

type Read = { readonly code: Barcode; readonly at: number }

/** A scanner spike, not a feature.
 *
 * The one part of barcode scanning that cannot be judged from the code is how
 * a library copes with a crumpled EAN-13 under supermarket lighting on the
 * phone that is actually in the trolley. This page does nothing but open the
 * camera and print what it reads, so that question can be answered in a shop
 * before the rest is built on top of the answer.
 *
 * It reports why it failed as well as what it read: on iOS a refused camera and
 * an insecure context look identical from the outside, and they need different
 * fixes. */
const ScanSpikePage = () => {
	const video = useRef<HTMLVideoElement>(null)
	const stop = useRef<(() => void) | null>(null)
	const [reads, setReads] = useState<ReadonlyArray<Read>>([])
	const [error, setError] = useState<string | null>(null)
	const [scanning, setScanning] = useState(false)

	const secure = typeof window !== 'undefined' && window.isSecureContext
	const hasCameraApi = typeof navigator !== 'undefined' && Boolean(navigator.mediaDevices?.getUserMedia)

	const starting = useRef(false)

	const start = useCallback(async () => {
		if (!video.current || starting.current || stop.current) {
			return
		}
		starting.current = true
		setError(null)
		const result = await createZxingBarcodeScanner().scan(
			video.current,
			code => {
				setReads(current => [{ code, at: Date.now() }, ...current].slice(0, 20))
			},
			scanError => {
				stop.current = null
				setScanning(false)
				setError(scanError.message)
			},
		)
		starting.current = false
		if (!result.success) {
			setError(result.error.message)
			return
		}
		stop.current = result.value
		setScanning(true)
	}, [])

	const halt = useCallback(() => {
		stop.current?.()
		stop.current = null
		setScanning(false)
	}, [])

	// The camera keeps running until it is told not to, so leaving the page has
	// to release it or the light stays on.
	useEffect(() => () => stop.current?.(), [])

	return (
		<main className='mx-auto flex min-h-dvh w-full max-w-xl flex-col gap-4 p-4'>
			<h1 className='text-lg font-semibold'>Scanner spike</h1>

			<dl className='grid grid-cols-2 gap-x-4 gap-y-1 rounded-md border border-current/15 p-3 text-sm'>
				<dt className='opacity-70'>Secure context</dt>
				<dd className={secure ? 'text-journ-mint' : 'text-journ-coral'}>{secure ? 'yes' : 'no — camera blocked'}</dd>
				<dt className='opacity-70'>Camera API</dt>
				<dd className={hasCameraApi ? 'text-journ-mint' : 'text-journ-coral'}>
					{hasCameraApi ? 'available' : 'missing'}
				</dd>
				<dt className='opacity-70'>Native detector</dt>
				<dd>{typeof window !== 'undefined' && 'BarcodeDetector' in window ? 'available, unused' : 'missing'}</dd>
				<dt className='opacity-70'>Decoder</dt>
				<dd>zxing</dd>
			</dl>

			{/* playsInline or iOS takes the video fullscreen the moment it plays. */}
			<video ref={video} className='bg-journ-surface-dark aspect-[3/4] w-full rounded-lg object-cover' playsInline muted />

			<div className='flex gap-2'>
				{scanning ? (
					<HapticButton tone='outline' type='button' onClick={halt}>
						Stop
					</HapticButton>
				) : (
					<HapticButton type='button' onClick={() => void start()} disabled={!secure || !hasCameraApi}>
						Start camera
					</HapticButton>
				)}
				<HapticButton tone='ghost' type='button' onClick={() => setReads([])}>
					Clear
				</HapticButton>
			</div>

			{error ? <p className='text-journ-coral text-sm'>{error}</p> : null}

			<div>
				<p className='text-sm opacity-70'>{reads.length} read</p>
				<ul className='mt-2 grid gap-1'>
					{reads.map(read => (
						<li
							key={`${read.code}-${read.at}`}
							className='flex items-baseline justify-between gap-3 rounded-md border border-current/10 px-3 py-2'
						>
							<span className='font-mono text-sm'>{read.code}</span>
							<span className='text-xs opacity-60'>{new Date(read.at).toLocaleTimeString()}</span>
						</li>
					))}
				</ul>
			</div>
		</main>
	)
}

export const Route = createFileRoute('/scan-spike')({
	component: ScanSpikePage,
})
